import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Upload, CheckCircle, AlertCircle, Copy } from "lucide-react";
import { S3_IMAGES } from '../config/s3Images';

export default function OrderConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Prefer state (from Checkout), but fall back to /order-confirmation/:orderId
  const orderId = location.state?.orderId ?? params.orderId;

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  // Copy link functionality
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadOrder() {
      try {
        if (!orderId) {
          navigate("/shop");
          return;
        }

        const base = process.env.REACT_APP_API_BASE;
        const res = await fetch(`${base}/api/orders/${orderId}`, {
          credentials: "include",
        });
        
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("Backend endpoint not configured correctly");
          navigate("/shop");
          return;
        }
        
        const data = await res.json();

        if (!res.ok || !data.success) {
          console.error("Load order failed:", data);
          navigate("/shop");
          return;
        }

        setOrderData(data.order);
        if (data.order.paymentProofSubmitted) {
          setUploadSuccess(true);
        }
      } catch (err) {
        console.error(err);
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    }

    loadOrder();
  }, [orderId, navigate]);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/order-confirmation/${orderId}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a valid image file (JPG, PNG, or WEBP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setUploadError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setFilePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPaymentProof = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const base = process.env.REACT_APP_API_BASE;
      const formData = new FormData();
      formData.append('paymentScreenshot', selectedFile);
      formData.append('orderId', orderId);

      const res = await fetch(`${base}/api/orders/${orderId}/payment-proof`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Backend endpoint not found. Please ensure the /api/orders/:orderId/payment-proof endpoint is configured.");
      }

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      setUploadSuccess(true);
      setSelectedFile(null);
      setFilePreview(null);
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Failed to upload payment proof. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }
  
  if (!orderData) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Order Confirmation</h1>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-6 space-y-3">
            <p className="text-gray-800 font-semibold">Thank you for your order!</p>

            <p className="text-gray-700 text-sm">
              Your order will only be confirmed once payment has been verified. Once verified, you will
              receive a payment verification email in your inbox. If you do not receive the email within
              3 working days, please contact us via email at{" "}
              <a
                href="mailto:AFTH-SALES@e.ntu.edu.sg"
                className="text-teal-600 underline font-medium"
              >
                AFTH-SALES@e.ntu.edu.sg
              </a>
              .
            </p>

            <p className="text-gray-700 text-sm">
              {orderData.items?.some(item => item.itemType === 'event') && <>During the Main Carnival, please present your payment verification email to the ticketing booth to redeem your physical ticket and goodie bag. </>}
              {orderData.items?.some(item => item.itemType === 'product') && <>Merchandise is pre-order only. NTU self-collection is on 25–27 January 2027, 1–5 PM, at the Student Activities Centre. Bring your order email showing your name and order number. Tracked delivery is expected in January 2027. </>}
              Please save a copy of your order for your own safekeeping.
            </p>
          </div>

          {/* Save This Page Banner */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-teal-800 font-semibold text-sm flex items-center gap-2">
                <Copy className="w-4 h-4" />
                📎 Note: Copy this page to upload your payment proof later
              </p>
              <p className="text-teal-700 text-xs mt-1 break-all">
                {`${window.location.origin}/order-confirmation/${orderId}`}
              </p>
            </div>
            <button
              onClick={handleCopyLink}
              className="shrink-0 px-4 py-2 bg-teal-400 hover:bg-teal-500 text-white text-sm font-medium rounded transition-colors flex items-center gap-2"
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Link
                </>
              )}
            </button>
          </div>

          {/* Order Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6 border-t border-b border-gray-200">
            <div>
              <div className="text-sm text-gray-500 mb-1">Order Number</div>
              <div className="font-semibold text-gray-900">{orderData.orderNumber}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Date</div>
              <div className="font-semibold text-gray-900">{orderData.date}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Total</div>
              <div className="font-semibold text-gray-900">${orderData.total.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Payment Method</div>
              <div className="font-semibold text-gray-900">{orderData.paymentMethod}</div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="mt-6 space-y-4">
            <p className="text-gray-700 font-medium">
              To complete your order, please transfer the total amount as stated within 24 hours:
            </p>

            <p className="text-gray-700">
              Scan PayNow QR code below and include your order number in the comments (e.g. order #
              {orderData.orderNumber})
            </p>

            {/* PayNow QR Code */}
            <div className="bg-gray-100 rounded-lg p-8 flex items-center justify-center">
              <div className="text-center">
                <img
                  src={S3_IMAGES.PAYNOW_QR}
                  alt="PayNow QR Code"
                  className="w-48 h-48 object-contain rounded mx-auto mb-2"
                />
                <p className="text-sm text-gray-500">Scan to PayNow</p>
              </div>
            </div>

            {/* Payment Screenshot Upload Section */}
            <div className="bg-white border-2 border-teal-200 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-teal-500" />
                Upload Payment Proof
              </h3>

              {uploadSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-green-800 font-semibold mb-2">Payment Proof Submitted Successfully!</h4>
                      <p className="text-green-700 text-sm mb-3">
                        Your payment screenshot has been received and is pending verification.
                      </p>
                      <div className="bg-white rounded p-4 border border-green-200">
                        <div className="text-sm space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className="text-orange-600 font-medium">Payment Pending Verification</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Order Number:</span>
                            <span className="text-gray-900 font-medium">#{orderData.orderNumber}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 text-sm text-gray-700">
                        <p className="font-medium mb-2">What happens next?</p>
                        <ul className="space-y-1 ml-4 list-disc">
                          <li>Our team will verify your payment within 3 working days</li>
                          <li>You'll receive a confirmation email once verified</li>
                          <li>The confirmation email will contain your ticket/order details</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    After making your payment via PayNow, please upload a screenshot of your transaction here.
                  </p>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                    <input
                      type="file"
                      id="payment-screenshot"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploading}
                    />
                    <label
                      htmlFor="payment-screenshot"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-3" />
                      <span className="text-sm font-medium text-gray-700 mb-1">
                        Click to upload payment screenshot
                      </span>
                      <span className="text-xs text-gray-500">
                        PNG, JPG, WEBP up to 5MB
                      </span>
                    </label>
                  </div>

                  {filePreview && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        <img
                          src={filePreview}
                          alt="Payment screenshot preview"
                          className="w-32 h-32 object-cover rounded border border-gray-300"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {selectedFile?.name}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">
                            {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                          <button
                            onClick={() => {
                              setSelectedFile(null);
                              setFilePreview(null);
                            }}
                            className="text-xs text-red-500 hover:text-red-700"
                            disabled={uploading}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{uploadError}</p>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Important:</span> Please ensure your screenshot clearly shows:
                    </p>
                    <ul className="text-sm text-gray-700 mt-2 ml-4 space-y-1 list-disc">
                      <li>Transaction amount: <span className="font-semibold">${orderData.total.toFixed(2)}</span></li>
                      <li>Reference/Comments: <span className="font-semibold">Order #{orderData.orderNumber}</span></li>
                      <li>Date and time of transaction</li>
                      <li>PayNow reference/comments showing order number</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 font-medium mb-1">
                      ⚠️ Action Required
                    </p>
                    <p className="text-sm text-yellow-800">
                      Please upload your payment proof within 24 hours to avoid order cancellation.
                      You can return to this page anytime using the link in your order confirmation email.
                    </p>
                  </div>

                  <button
                    onClick={handleSubmitPaymentProof}
                    disabled={!selectedFile || uploading}
                    className="w-full bg-teal-400 hover:bg-teal-500 text-white font-medium py-3 rounded transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Submit Payment Proof
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Details Section */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Order details</h2>

          <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Product</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orderData.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4">
                      <div className="text-gray-900">{item.name}</div>
                      {item.size && <div className="text-sm text-gray-500">Size: {item.size}</div>}
                      {item.quantity > 1 && (
                        <div className="text-sm text-gray-500">Quantity: {item.quantity}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      ${(item.price * item.quantity).toFixed(2)}
                    </td>
                  </tr>
                ))}

                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-700">Subtotal:</td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    ${orderData.subtotal.toFixed(2)}
                  </td>
                </tr>

                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-700">
                    Shipping ({orderData.shippingMethod}):
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {orderData.shipping === 0 ? "$0" : `$${orderData.shipping.toFixed(2)}`}
                  </td>
                </tr>

                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-700">Payment Method:</td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">{orderData.paymentMethod}</td>
                </tr>

                <tr className="bg-gray-50 border-t-2 border-gray-300">
                  <td className="px-6 py-4 font-bold text-gray-900">Total:</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900 text-lg">
                    ${orderData.total.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate("/events")}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded transition-colors"
            >
              Continue Shopping
            </button>
            
            {uploadSuccess && (
              <button
                onClick={() => window.print()}
                className="px-6 py-3 bg-teal-400 hover:bg-teal-500 text-white font-medium rounded transition-colors"
              >
                Print Order Details
              </button>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Questions about your order? Email us at{" "}
            <a href="mailto:AFTH-SALES@e.ntu.edu.sg" className="text-teal-500 hover:text-teal-600 underline">
              AFTH-SALES@e.ntu.edu.sg
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}